import React from "react";
import { Box, Typography } from "@mui/material";

/** Keys that map to status tabs (must stay in sync with AdminConsultations STATUS_FILTER_KEY_LETTERS + tab count). */
const TAB_KEYS = ["Q", "W", "E", "R", "T", "Y", "U", "I"];

/** Home-row keys for row actions (left → right, matches ConsultationsTable). */
const ACTION_KEYS = ["A", "S", "D", "F", "G", "H", "J", "K"];

/**
 * Wireframe-style diagram: page regions line up with keyboard rows (tabs, numbers, actions).
 */
export default function ConsultationsHelpDiagram({ t }) {
  return (
    <Box component="figure" sx={{ my: 2, mx: 0 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>
        {t("Consultations help diagram title")}
      </Typography>

      <Box
        dir="ltr"
        sx={{
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.04)"
              : "rgba(15, 23, 42, 0.04)",
        }}
      >
        {/* Top bar: menu + search */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
            pb: 1.5,
            borderBottom: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 36,
              height: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid",
              borderColor: "text.disabled",
              borderRadius: 1,
              fontSize: 14,
            }}
          >
            ☰
          </Box>
          <Box
            aria-hidden
            sx={{
              flex: 1,
              height: 30,
              display: "flex",
              alignItems: "center",
              px: 1.25,
              border: "1px solid",
              borderColor: "text.disabled",
              borderRadius: 1,
              fontSize: 11,
              color: "text.secondary",
            }}
          >
            {t("Consultations help diagram search placeholder")}
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontFamily: "ui-monospace, monospace",
              fontWeight: 700,
              color: "primary.main",
              minWidth: 20,
              textAlign: "center",
            }}
          >
            /
          </Typography>
        </Box>

        {/* Status tabs + Q–I */}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
          {t("Consultations help diagram row tabs")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            mb: 1.75,
          }}
        >
          {TAB_KEYS.map((k) => (
            <Box
              key={k}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.35,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "ui-monospace, monospace",
                  fontWeight: 700,
                  color: "primary.main",
                  lineHeight: 1,
                }}
              >
                {k}
              </Typography>
              <Box
                sx={{
                  minWidth: 34,
                  height: 22,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 0.75,
                  bgcolor: "background.paper",
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Table + number row */}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
          {t("Consultations help diagram row numbers")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1.25,
            mb: 1.75,
          }}
        >
          {[1, 2, 3].map((n) => (
            <Box
              key={n}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.35,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "ui-monospace, monospace",
                  fontWeight: 600,
                  color: "primary.main",
                  width: 18,
                  textAlign: "center",
                }}
              >
                {n}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: 10,
                  borderRadius: 0.5,
                  bgcolor: "action.hover",
                }}
              />
            </Box>
          ))}
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.75 }}>
            …
          </Typography>
        </Box>

        {/* Row actions + A–K */}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
          {t("Consultations help diagram row actions")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            justifyContent: "flex-start",
          }}
        >
          {ACTION_KEYS.map((k) => (
            <Box
              key={k}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.35,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "ui-monospace, monospace",
                  fontWeight: 700,
                  color: "secondary.main",
                  lineHeight: 1,
                }}
              >
                {k}
              </Typography>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  border: "1px solid",
                  borderColor: "secondary.main",
                  borderRadius: 0.75,
                  opacity: 0.85,
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        component="figcaption"
        sx={{ display: "block", mt: 1.5, lineHeight: 1.55 }}
      >
        {t("Consultations help diagram caption")}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1, lineHeight: 1.5, fontStyle: "italic" }}
      >
        {t("Consultations help keyboard latin note")}
      </Typography>
    </Box>
  );
}
